export type ContactAction = 'whatsapp' | 'call' | 'email';

export type CTAAction =
  | 'booking' | 'menu' | 'quote' | 'contact' | 'shop'
  | 'save_contact' | 'portfolio' | 'donate' | 'enrol'
  | 'request' | 'follow' | 'consult';

export type ContentBlock =
  | 'specialisation' | 'hours' | 'location' | 'insurance' | 'reviews'
  | 'menu' | 'gallery' | 'reservation' | 'delivery'
  | 'services' | 'portfolio' | 'testimonials' | 'rate' | 'calendar'
  | 'products' | 'about' | 'team'
  | 'featured_products' | 'categories' | 'shop_link'
  | 'bio' | 'skills' | 'social_links' | 'cv'
  | 'case_studies' | 'publications' | 'court_areas'
  | 'tax_services' | 'audit_services' | 'compliance'
  | 'listings' | 'properties' | 'map'
  | 'coverage' | 'claims' | 'calculator'
  | 'fund_types' | 'returns' | 'risk_profile'
  | 'projects' | 'blueprints' | 'certifications'
  | 'engineering_services' | 'standards'
  | 'coaching_topics' | 'session_types' | 'results'
  | 'bakery_menu' | 'daily_specials' | 'pre_order'
  | 'room_types' | 'amenities' | 'booking_widget'
  | 'event_packages' | 'capacity' | 'enquiry'
  | 'catering_menu' | 'minimum_order'
  | 'style_portfolio' | 'pricing_menu' | 'booking_slots'
  | 'dental_services' | 'x_ray' | 'pain_assessment'
  | 'drug_catalogue' | 'prescription_upload' | 'delivery_radius'
  | 'rehab_services' | 'assessment_form'
  | 'mental_health_topics' | 'session_booking' | 'confidentiality'
  | 'pet_types' | 'vet_services' | 'emergency_line'
  | 'photo_gallery' | 'packages_pricing' | 'equipment_list'
  | 'video_reel' | 'editing_samples'
  | 'artwork_gallery' | 'commissions' | 'shop'
  | 'music_player' | 'gig_calendar' | 'booking_enquiry'
  | 'content_links' | 'brand_kit' | 'collaboration'
  | 'podcast_episodes' | 'subscribe_links' | 'sponsors'
  | 'books' | 'writing_samples' | 'newsletter'
  | 'subject_areas' | 'availability' | 'trial_class'
  | 'courses' | 'curriculum' | 'accreditation'
  | 'training_calendar' | 'corporate_enquiry'
  | 'service_area' | 'call_out_fee' | 'emergency_availability'
  | 'trade_licence' | 'insurance_cert'
  | 'fleet' | 'zones' | 'freight_enquiry'
  | 'company_profile' | 'divisions' | 'investor_relations'
  | 'mission' | 'impact_report' | 'donate_link'
  | 'departments' | 'public_services' | 'complaints'
  | 'freelance_services' | 'rate_card' | 'availability_calendar'
  | 'degree' | 'research' | 'internship'
  | 'treatment_menu' | 'therapists' | 'packages'
  | 'classes' | 'schedule' | 'membership'
  | 'nutrition_plans' | 'macro_calculator'
  | 'equipment' | 'training_programs';

export interface IdentityType {
  id: string;
  label: string;
  icon: string;
  category: string;
  primaryCTA: { label: string; action: CTAAction };
  accentColor: string;
  verifiedBadgeLabel: string;
  defaultBlocks: ContentBlock[];
  contactBar: ContactAction[];
  themeDefault: string;
}

export const IDENTITY_CATEGORIES = [
  'Healthcare',
  'Legal & Finance',
  'Professional Services',
  'Food & Hospitality',
  'Retail',
  'Fitness & Wellness',
  'Creative',
  'Creator Economy',
  'Education',
  'Trade & Services',
  'Corporate',
  'Personal',
] as const;

export const IDENTITY_TYPES: IdentityType[] = [

  // ── HEALTHCARE ──────────────────────────────────────────────────────────
  {
    id: 'doctor',
    label: 'Doctor',
    icon: '🏥',
    category: 'Healthcare',
    primaryCTA: { label: 'Book Appointment', action: 'booking' },
    accentColor: '#0D9488',
    verifiedBadgeLabel: 'Verified Medical Professional',
    defaultBlocks: ['specialisation', 'hours', 'location', 'insurance', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'forest',
  },
  {
    id: 'dentist',
    label: 'Dentist',
    icon: '🦷',
    category: 'Healthcare',
    primaryCTA: { label: 'Book Appointment', action: 'booking' },
    accentColor: '#0EA5E9',
    verifiedBadgeLabel: 'Verified Dental Professional',
    defaultBlocks: ['dental_services', 'hours', 'location', 'x_ray', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'arctic',
  },
  {
    id: 'pharmacist',
    label: 'Pharmacist',
    icon: '💊',
    category: 'Healthcare',
    primaryCTA: { label: 'Upload Prescription', action: 'request' },
    accentColor: '#16A34A',
    verifiedBadgeLabel: 'Verified Pharmacy',
    defaultBlocks: ['drug_catalogue', 'hours', 'location', 'prescription_upload', 'delivery_radius'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'matcha',
  },
  {
    id: 'physiotherapist',
    label: 'Physiotherapist',
    icon: '🦴',
    category: 'Healthcare',
    primaryCTA: { label: 'Book Session', action: 'booking' },
    accentColor: '#0891B2',
    verifiedBadgeLabel: 'Verified Physiotherapist',
    defaultBlocks: ['rehab_services', 'hours', 'location', 'assessment_form', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'ocean',
  },
  {
    id: 'psychologist',
    label: 'Psychologist',
    icon: '🧠',
    category: 'Healthcare',
    primaryCTA: { label: 'Book Consultation', action: 'booking' },
    accentColor: '#7C3AED',
    verifiedBadgeLabel: 'Verified Mental Health Professional',
    defaultBlocks: ['mental_health_topics', 'session_booking', 'confidentiality', 'hours', 'location'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'midnight',
  },
  {
    id: 'veterinarian',
    label: 'Veterinarian',
    icon: '🐾',
    category: 'Healthcare',
    primaryCTA: { label: 'Book Appointment', action: 'booking' },
    accentColor: '#059669',
    verifiedBadgeLabel: 'Verified Veterinary Professional',
    defaultBlocks: ['pet_types', 'vet_services', 'hours', 'location', 'emergency_line'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'forest',
  },

  // ── LEGAL & FINANCE ──────────────────────────────────────────────────────
  {
    id: 'lawyer',
    label: 'Lawyer',
    icon: '⚖️',
    category: 'Legal & Finance',
    primaryCTA: { label: 'Get Legal Advice', action: 'consult' },
    accentColor: '#1E3A5F',
    verifiedBadgeLabel: 'Verified Legal Professional',
    defaultBlocks: ['case_studies', 'court_areas', 'publications', 'location', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'slate',
  },
  {
    id: 'ca_auditor',
    label: 'CA / Auditor',
    icon: '📊',
    category: 'Legal & Finance',
    primaryCTA: { label: 'Book Consultation', action: 'consult' },
    accentColor: '#1E40AF',
    verifiedBadgeLabel: 'Verified Chartered Accountant',
    defaultBlocks: ['tax_services', 'audit_services', 'compliance', 'location', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'slate',
  },
  {
    id: 'financial_advisor',
    label: 'Financial Advisor',
    icon: '💹',
    category: 'Legal & Finance',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#D97706',
    verifiedBadgeLabel: 'Verified Financial Advisor',
    defaultBlocks: ['fund_types', 'returns', 'risk_profile', 'services', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'gold',
  },
  {
    id: 'insurance_agent',
    label: 'Insurance Agent',
    icon: '🛡️',
    category: 'Legal & Finance',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#0369A1',
    verifiedBadgeLabel: 'Verified Insurance Professional',
    defaultBlocks: ['coverage', 'claims', 'calculator', 'services', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'ocean',
  },
  {
    id: 'real_estate_agent',
    label: 'Real Estate Agent',
    icon: '🏠',
    category: 'Legal & Finance',
    primaryCTA: { label: 'View Listings', action: 'menu' },
    accentColor: '#B45309',
    verifiedBadgeLabel: 'Verified Real Estate Professional',
    defaultBlocks: ['listings', 'properties', 'map', 'services', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'gold',
  },

  // ── PROFESSIONAL SERVICES ────────────────────────────────────────────────
  {
    id: 'consultant',
    label: 'Consultant',
    icon: '💼',
    category: 'Professional Services',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#1E40AF',
    verifiedBadgeLabel: 'Verified Consultant',
    defaultBlocks: ['services', 'portfolio', 'testimonials', 'rate', 'calendar'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'slate',
  },
  {
    id: 'architect',
    label: 'Architect',
    icon: '🏛️',
    category: 'Professional Services',
    primaryCTA: { label: 'View Projects', action: 'portfolio' },
    accentColor: '#475569',
    verifiedBadgeLabel: 'Verified Architect',
    defaultBlocks: ['projects', 'blueprints', 'certifications', 'location', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'slate',
  },
  {
    id: 'engineer',
    label: 'Engineer',
    icon: '⚙️',
    category: 'Professional Services',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#334155',
    verifiedBadgeLabel: 'Verified Engineer',
    defaultBlocks: ['engineering_services', 'certifications', 'standards', 'projects', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'slate',
  },
  {
    id: 'coach_mentor',
    label: 'Coach / Mentor',
    icon: '🎯',
    category: 'Professional Services',
    primaryCTA: { label: 'Book Session', action: 'booking' },
    accentColor: '#7C3AED',
    verifiedBadgeLabel: 'Verified Coach',
    defaultBlocks: ['coaching_topics', 'session_types', 'results', 'testimonials', 'calendar'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'midnight',
  },

  // ── FOOD & HOSPITALITY ───────────────────────────────────────────────────
  {
    id: 'restaurant',
    label: 'Restaurant',
    icon: '🍽️',
    category: 'Food & Hospitality',
    primaryCTA: { label: 'View Menu', action: 'menu' },
    accentColor: '#DC2626',
    verifiedBadgeLabel: 'Verified Restaurant',
    defaultBlocks: ['menu', 'hours', 'location', 'gallery', 'reservation'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'sunset',
  },
  {
    id: 'cafe_bakery',
    label: 'Café / Bakery',
    icon: '☕',
    category: 'Food & Hospitality',
    primaryCTA: { label: 'View Menu', action: 'menu' },
    accentColor: '#92400E',
    verifiedBadgeLabel: 'Verified Café',
    defaultBlocks: ['bakery_menu', 'daily_specials', 'hours', 'location', 'pre_order'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'gold',
  },
  {
    id: 'hotel_resort',
    label: 'Hotel / Resort',
    icon: '🏨',
    category: 'Food & Hospitality',
    primaryCTA: { label: 'Book Room', action: 'booking' },
    accentColor: '#D97706',
    verifiedBadgeLabel: 'Verified Hotel',
    defaultBlocks: ['room_types', 'amenities', 'gallery', 'location', 'booking_widget'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'gold',
  },
  {
    id: 'event_venue',
    label: 'Event Venue',
    icon: '🎪',
    category: 'Food & Hospitality',
    primaryCTA: { label: 'Check Availability', action: 'booking' },
    accentColor: '#9333EA',
    verifiedBadgeLabel: 'Verified Event Venue',
    defaultBlocks: ['event_packages', 'capacity', 'gallery', 'location', 'enquiry'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'nebula',
  },
  {
    id: 'catering',
    label: 'Catering',
    icon: '🍱',
    category: 'Food & Hospitality',
    primaryCTA: { label: 'Request Quote', action: 'quote' },
    accentColor: '#EA580C',
    verifiedBadgeLabel: 'Verified Catering Service',
    defaultBlocks: ['catering_menu', 'gallery', 'minimum_order', 'location', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'sunset',
  },

  // ── RETAIL ───────────────────────────────────────────────────────────────
  {
    id: 'store',
    label: 'Store',
    icon: '🛒',
    category: 'Retail',
    primaryCTA: { label: 'Shop Now', action: 'shop' },
    accentColor: '#EA580C',
    verifiedBadgeLabel: 'Verified Store',
    defaultBlocks: ['featured_products', 'categories', 'about', 'reviews', 'shop_link'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'sunset',
  },
  {
    id: 'salon_barber',
    label: 'Salon / Barber',
    icon: '✂️',
    category: 'Retail',
    primaryCTA: { label: 'Book Appointment', action: 'booking' },
    accentColor: '#EC4899',
    verifiedBadgeLabel: 'Verified Salon',
    defaultBlocks: ['style_portfolio', 'pricing_menu', 'booking_slots', 'location', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'rose',
  },
  {
    id: 'spa_wellness',
    label: 'Spa / Wellness',
    icon: '🧖',
    category: 'Retail',
    primaryCTA: { label: 'Book Treatment', action: 'booking' },
    accentColor: '#0D9488',
    verifiedBadgeLabel: 'Verified Spa',
    defaultBlocks: ['treatment_menu', 'therapists', 'packages', 'hours', 'location'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'forest',
  },

  // ── FITNESS & WELLNESS ───────────────────────────────────────────────────
  {
    id: 'gym_fitness',
    label: 'Gym / Fitness Trainer',
    icon: '💪',
    category: 'Fitness & Wellness',
    primaryCTA: { label: 'Join Now', action: 'enrol' },
    accentColor: '#DC2626',
    verifiedBadgeLabel: 'Verified Fitness Professional',
    defaultBlocks: ['classes', 'schedule', 'membership', 'equipment', 'location'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'sunset',
  },
  {
    id: 'yoga_meditation',
    label: 'Yoga / Meditation',
    icon: '🧘',
    category: 'Fitness & Wellness',
    primaryCTA: { label: 'Book Class', action: 'booking' },
    accentColor: '#059669',
    verifiedBadgeLabel: 'Verified Yoga Instructor',
    defaultBlocks: ['classes', 'schedule', 'packages', 'location', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'forest',
  },
  {
    id: 'nutritionist',
    label: 'Nutritionist',
    icon: '🥗',
    category: 'Fitness & Wellness',
    primaryCTA: { label: 'Book Consultation', action: 'consult' },
    accentColor: '#16A34A',
    verifiedBadgeLabel: 'Verified Nutritionist',
    defaultBlocks: ['nutrition_plans', 'macro_calculator', 'services', 'testimonials', 'calendar'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'matcha',
  },

  // ── CREATIVE ─────────────────────────────────────────────────────────────
  {
    id: 'photographer',
    label: 'Photographer',
    icon: '📷',
    category: 'Creative',
    primaryCTA: { label: 'View Portfolio', action: 'portfolio' },
    accentColor: '#1E293B',
    verifiedBadgeLabel: 'Verified Photographer',
    defaultBlocks: ['photo_gallery', 'packages_pricing', 'equipment_list', 'reviews', 'calendar'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'midnight',
  },
  {
    id: 'videographer',
    label: 'Videographer',
    icon: '🎬',
    category: 'Creative',
    primaryCTA: { label: 'View Reel', action: 'portfolio' },
    accentColor: '#7C3AED',
    verifiedBadgeLabel: 'Verified Videographer',
    defaultBlocks: ['video_reel', 'packages_pricing', 'editing_samples', 'reviews', 'calendar'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'nebula',
  },
  {
    id: 'artist_designer',
    label: 'Artist / Designer',
    icon: '🎨',
    category: 'Creative',
    primaryCTA: { label: 'View Work', action: 'portfolio' },
    accentColor: '#EC4899',
    verifiedBadgeLabel: 'Verified Creative Professional',
    defaultBlocks: ['artwork_gallery', 'commissions', 'shop', 'about', 'social_links'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'rose',
  },
  {
    id: 'musician_dj',
    label: 'Musician / DJ',
    icon: '🎵',
    category: 'Creative',
    primaryCTA: { label: 'Book Now', action: 'booking' },
    accentColor: '#8B5CF6',
    verifiedBadgeLabel: 'Verified Artist',
    defaultBlocks: ['music_player', 'gig_calendar', 'packages_pricing', 'booking_enquiry', 'social_links'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'nebula',
  },

  // ── CREATOR ECONOMY ──────────────────────────────────────────────────────
  {
    id: 'content_creator',
    label: 'Content Creator',
    icon: '📱',
    category: 'Creator Economy',
    primaryCTA: { label: 'Follow Me', action: 'follow' },
    accentColor: '#F43F5E',
    verifiedBadgeLabel: 'Verified Creator',
    defaultBlocks: ['content_links', 'social_links', 'brand_kit', 'collaboration', 'about'],
    contactBar: ['whatsapp', 'email', 'call'],
    themeDefault: 'rose',
  },
  {
    id: 'podcaster',
    label: 'Podcaster',
    icon: '🎙️',
    category: 'Creator Economy',
    primaryCTA: { label: 'Listen Now', action: 'follow' },
    accentColor: '#6D28D9',
    verifiedBadgeLabel: 'Verified Podcaster',
    defaultBlocks: ['podcast_episodes', 'subscribe_links', 'sponsors', 'about', 'social_links'],
    contactBar: ['email', 'whatsapp', 'call'],
    themeDefault: 'midnight',
  },
  {
    id: 'writer_author',
    label: 'Writer / Author',
    icon: '✍️',
    category: 'Creator Economy',
    primaryCTA: { label: 'Read My Work', action: 'portfolio' },
    accentColor: '#92400E',
    verifiedBadgeLabel: 'Verified Author',
    defaultBlocks: ['books', 'writing_samples', 'newsletter', 'about', 'social_links'],
    contactBar: ['email', 'whatsapp', 'call'],
    themeDefault: 'gold',
  },

  // ── EDUCATION ────────────────────────────────────────────────────────────
  {
    id: 'teacher_tutor',
    label: 'Teacher / Tutor',
    icon: '📚',
    category: 'Education',
    primaryCTA: { label: 'Book Trial Class', action: 'booking' },
    accentColor: '#2563EB',
    verifiedBadgeLabel: 'Verified Educator',
    defaultBlocks: ['subject_areas', 'availability', 'trial_class', 'reviews', 'location'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'ocean',
  },
  {
    id: 'school_institute',
    label: 'School / Institute',
    icon: '🏫',
    category: 'Education',
    primaryCTA: { label: 'Apply Now', action: 'enrol' },
    accentColor: '#1D4ED8',
    verifiedBadgeLabel: 'Verified Educational Institution',
    defaultBlocks: ['courses', 'curriculum', 'accreditation', 'location', 'about'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'ocean',
  },
  {
    id: 'training_centre',
    label: 'Training Centre',
    icon: '🎓',
    category: 'Education',
    primaryCTA: { label: 'Enrol Now', action: 'enrol' },
    accentColor: '#0369A1',
    verifiedBadgeLabel: 'Verified Training Provider',
    defaultBlocks: ['courses', 'training_calendar', 'corporate_enquiry', 'certifications', 'location'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'ocean',
  },

  // ── TRADE & SERVICES ─────────────────────────────────────────────────────
  {
    id: 'electrician',
    label: 'Electrician',
    icon: '⚡',
    category: 'Trade & Services',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#CA8A04',
    verifiedBadgeLabel: 'Verified Electrician',
    defaultBlocks: ['services', 'service_area', 'call_out_fee', 'emergency_availability', 'reviews'],
    contactBar: ['call', 'whatsapp', 'email'],
    themeDefault: 'gold',
  },
  {
    id: 'plumber',
    label: 'Plumber',
    icon: '🔧',
    category: 'Trade & Services',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#0369A1',
    verifiedBadgeLabel: 'Verified Plumber',
    defaultBlocks: ['services', 'service_area', 'call_out_fee', 'emergency_availability', 'reviews'],
    contactBar: ['call', 'whatsapp', 'email'],
    themeDefault: 'ocean',
  },
  {
    id: 'carpenter',
    label: 'Carpenter',
    icon: '🪚',
    category: 'Trade & Services',
    primaryCTA: { label: 'Get Quote', action: 'quote' },
    accentColor: '#92400E',
    verifiedBadgeLabel: 'Verified Carpenter',
    defaultBlocks: ['services', 'portfolio', 'service_area', 'trade_licence', 'reviews'],
    contactBar: ['call', 'whatsapp', 'email'],
    themeDefault: 'gold',
  },
  {
    id: 'driver_logistics',
    label: 'Driver / Logistics',
    icon: '🚛',
    category: 'Trade & Services',
    primaryCTA: { label: 'Request Delivery', action: 'request' },
    accentColor: '#334155',
    verifiedBadgeLabel: 'Verified Transport Provider',
    defaultBlocks: ['fleet', 'zones', 'freight_enquiry', 'service_area', 'reviews'],
    contactBar: ['call', 'whatsapp', 'email'],
    themeDefault: 'slate',
  },

  // ── CORPORATE ────────────────────────────────────────────────────────────
  {
    id: 'business',
    label: 'Business',
    icon: '🏢',
    category: 'Corporate',
    primaryCTA: { label: 'Contact Us', action: 'contact' },
    accentColor: '#475569',
    verifiedBadgeLabel: 'Verified Business',
    defaultBlocks: ['products', 'hours', 'location', 'about', 'reviews'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'slate',
  },
  {
    id: 'startup',
    label: 'Startup',
    icon: '🚀',
    category: 'Corporate',
    primaryCTA: { label: 'Learn More', action: 'contact' },
    accentColor: '#6D28D9',
    verifiedBadgeLabel: 'Verified Startup',
    defaultBlocks: ['about', 'team', 'services', 'portfolio', 'social_links'],
    contactBar: ['email', 'whatsapp', 'call'],
    themeDefault: 'midnight',
  },
  {
    id: 'ngo_trust',
    label: 'NGO / Trust',
    icon: '❤️',
    category: 'Corporate',
    primaryCTA: { label: 'Donate Now', action: 'donate' },
    accentColor: '#DC2626',
    verifiedBadgeLabel: 'Verified Non-Profit Organisation',
    defaultBlocks: ['mission', 'impact_report', 'donate_link', 'about', 'social_links'],
    contactBar: ['whatsapp', 'email', 'call'],
    themeDefault: 'sunset',
  },
  {
    id: 'government',
    label: 'Government',
    icon: '🏛️',
    category: 'Corporate',
    primaryCTA: { label: 'Access Services', action: 'menu' },
    accentColor: '#1E3A5F',
    verifiedBadgeLabel: 'Verified Government Entity',
    defaultBlocks: ['departments', 'public_services', 'hours', 'location', 'complaints'],
    contactBar: ['call', 'email', 'whatsapp'],
    themeDefault: 'slate',
  },

  // ── PERSONAL ─────────────────────────────────────────────────────────────
  {
    id: 'personal',
    label: 'Personal',
    icon: '👤',
    category: 'Personal',
    primaryCTA: { label: 'Save Contact', action: 'save_contact' },
    accentColor: '#7C3AED',
    verifiedBadgeLabel: 'CommonQR Member',
    defaultBlocks: ['bio', 'skills', 'social_links', 'portfolio', 'about'],
    contactBar: ['whatsapp', 'call', 'email'],
    themeDefault: 'midnight',
  },
  {
    id: 'freelancer',
    label: 'Freelancer',
    icon: '🧑‍💻',
    category: 'Personal',
    primaryCTA: { label: 'Hire Me', action: 'quote' },
    accentColor: '#0891B2',
    verifiedBadgeLabel: 'Verified Freelancer',
    defaultBlocks: ['freelance_services', 'portfolio', 'rate_card', 'availability_calendar', 'reviews'],
    contactBar: ['whatsapp', 'email', 'call'],
    themeDefault: 'ocean',
  },
  {
    id: 'student',
    label: 'Student',
    icon: '🎒',
    category: 'Personal',
    primaryCTA: { label: 'Connect', action: 'save_contact' },
    accentColor: '#2563EB',
    verifiedBadgeLabel: 'CommonQR Member',
    defaultBlocks: ['bio', 'degree', 'skills', 'research', 'internship'],
    contactBar: ['whatsapp', 'email', 'call'],
    themeDefault: 'ocean',
  },
];

export const getTypeById = (id: string): IdentityType | undefined =>
  IDENTITY_TYPES.find(t => t.id === id);

export const getTypesByCategory = (category: string): IdentityType[] =>
  IDENTITY_TYPES.filter(t => t.category === category);

export const getAllCategories = (): string[] =>
  [...new Set(IDENTITY_TYPES.map(t => t.category))];